import { getCurrentUser, supabase } from './supabase'
import { sendNewTicketNotification, sendTicketResponseNotification } from './services/supportTicketNotificationService'
import { getSessionHeaders } from './serverSession'

// API helper for support system operations - NO getServiceRoleClient() in browser
async function callSupportAPI(operation: string, data: Record<string, any> = {}): Promise<any> {
 const response = await fetch('/api/user/support-system', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ operation, data })
 });

 if (!response.ok) {
 throw new Error(`Support API error: ${response.status}`);
 }

 return response.json();
}

// Types
export interface SupportAgent {
 id: number
 user_id: number | null
 agent_name: string
 agent_email: string
 name?: string // legacy column, same as agent_name
 email?: string // legacy column, same as agent_email
 specialization: string // comma-separated string in DB
 department?: string // v2.9.55: primary department
 supported_languages?: string[] // v2.9.55: JSONB array of ISO language codes
 max_concurrent_chats: number
 priority_level: number
 is_active: boolean
 availability?: AgentAvailability[] | AgentAvailability
}

export interface AgentAvailability {
 id: number
 agent_id: number
 status: 'available' | 'busy' | 'away' | 'offline'
 current_chat_count: number
 max_concurrent_chats: number
 last_activity?: string
 status_message?: string
}

export interface SupportTicket {
 id: number
 ticket_number: string
 user_id: number
 user_type: 'shareholder' | 'affiliate'
 assigned_agent_id?: number
 title: string
 description?: string
 category?: string
 priority: 'low' | 'medium' | 'high' | 'urgent'
 status: 'open' | 'in_progress' | 'waiting_user' | 'resolved' | 'closed'
 tags?: string[]
 first_response_at?: string
 resolved_at?: string
 closed_at?: string
 satisfaction_rating?: number
 satisfaction_feedback?: string
 created_at: string
 updated_at: string
 agent?: SupportAgent
 user?: any
}

export interface ChatSession {
 id: number
 ticket_id: number
 user_id: number
 agent_id?: number
 session_type: 'support' | 'consultation' | 'training'
 status: 'active' | 'transferred' | 'ended'
 started_at: string
 ended_at?: string
 transfer_reason?: string
 session_rating?: number
 agent?: SupportAgent
 user?: any
}

export interface ChatMessage {
 id: string
 session_id: string
 ticket_id: string
 sender_type: 'user' | 'agent' | 'system'
 sender_id: number
 message_type: 'text' | 'file' | 'image' | 'system_notification'
 content: string
 file_url?: string
 file_name?: string
 file_size?: number
 is_read: boolean
 read_at?: string
 is_internal_note: boolean
 created_at: string
 sender?: any
}

export interface TrainingContent {
 id: string
 category_id: string
 title: string
 description?: string
 content_type: 'video' | 'document' | 'presentation' | 'quiz' | 'webinar'
 target_audience: 'shareholder' | 'affiliate' | 'both'
 content_url?: string
 thumbnail_url?: string
 duration_minutes?: number
 file_size?: number
 download_url?: string
 prerequisites?: string[]
 learning_objectives?: string[]
 difficulty_level: 'beginner' | 'intermediate' | 'advanced'
 tags?: string[]
 is_featured: boolean
 is_mandatory: boolean
 sort_order: number
 is_active: boolean
 created_at: string
 updated_at: string
 progress?: UserTrainingProgress
}

export interface UserTrainingProgress {
 id: string
 user_id: number
 content_id: string
 status: 'not_started' | 'in_progress' | 'completed' | 'failed'
 progress_percentage: number
 time_spent_minutes: number
 started_at?: string
 completed_at?: string
 last_accessed_at: string
 quiz_score?: number
 certificate_issued: boolean
 certificate_url?: string
 notes?: string
}

export interface ConsultationBooking {
 id: string
 user_id: number
 agent_id?: string
 consultation_type: 'investment_guidance' | 'business_opportunity' | 'technical_support'
 user_type: 'shareholder' | 'affiliate'
 title: string
 description?: string
 scheduled_at: string
 duration_minutes: number
 timezone: string
 status: 'scheduled' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'no_show'
 meeting_url?: string
 meeting_id?: string
 meeting_password?: string
 reminder_sent: boolean
 follow_up_required: boolean
 follow_up_notes?: string
 rating?: number
 feedback?: string
 created_at: string
 updated_at: string
 agent?: SupportAgent
 user?: any
}

// Utility Functions
export const generateTicketNumber = (): string => {
 const date = new Date()
 const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '')
 const random = Math.floor(Math.random() * 9999).toString().padStart(4, '0')
 return `TKT-${dateStr}-${random}`
}

export const getUserType = async (userId: number): Promise<'shareholder' | 'affiliate'> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('getUserType', { userId })
 const user = result.user

 if (user?.role === 'shareholder') return 'shareholder'
 if (user?.role === 'affiliate') return 'affiliate'
 if (user?.telegram_id) return 'affiliate'
 return 'shareholder' // Default fallback
 } catch (error) {
 console.error('Error determining user type:', error)
 return 'shareholder'
 }
}

export const getCurrentDbUserId = async (): Promise<number | null> => {
 try {
 const user = await getCurrentUser()
 const rawId = user?.database_user?.id || user?.user_metadata?.user_id || null

 if (rawId === null || rawId === undefined) {
 return null
 }

 // Handle numeric ID
 if (typeof rawId === 'number') {
 return rawId
 }

 // Handle string ID - remove "db_" prefix if present and parse
 if (typeof rawId === 'string') {
 const cleanedId = rawId.replace(/db_/g, '').trim()
 const numericId = parseInt(cleanedId, 10)
 if (!isNaN(numericId)) {
 return numericId
 }
 }

 console.error('Unable to parse user ID:', rawId)
 return null
 } catch (error) {
 console.error('Error getting current user ID:', error)
 return null
 }
}

// Agent Management

// Check if any support agents are currently online/available
export const checkAgentsOnline = async (): Promise<{ isOnline: boolean; agentCount: number }> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('checkAgentsOnline', {})

 if (!result.success) throw new Error(result.error)

 const onlineCount = result.availability?.length || 0
 return { isOnline: onlineCount > 0, agentCount: onlineCount }
 } catch (error) {
 console.error('Error checking agents online:', error)
 return { isOnline: false, agentCount: 0 }
 }
}

export const getAvailableAgents = async (userType?: 'shareholder' | 'affiliate'): Promise<SupportAgent[]> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('getAvailableAgents', { userType })

 if (!result.success) throw new Error(result.error)

 const agents = result.agents || []
 const availability = result.availability || []

 // Map availability to agents
 const agentsWithAvailability = agents.map((agent: any) => {
 const agentAvail = availability.find((a: any) => a.agent_id === agent.id)
 return { ...agent, availability: agentAvail ? [agentAvail] : [] }
 })

 // Filter by availability status
 return agentsWithAvailability.filter((agent: any) =>
 agent.availability?.[0]?.status === 'available' &&
 (agent.availability?.[0]?.current_chat_count || 0) < (agent.availability?.[0]?.max_concurrent_chats || 3)
 )
 } catch (error) {
 console.error('Error getting available agents:', error)
 return []
 }
}

/**
 * v2.9.55: Weighted skill-based agent routing.
 * Priority tiers:
 *  1. Exact department + language match  (weight 3)
 *  2. Department match, any language      (weight 2)
 *  3. General Support fallback            (weight 1)
 * Within a tier, agents are sorted by priority_level (lower = higher priority)
 * and then by current load (fewest active chats first).
 */
const scoreAgentForTicket = (
 agent: SupportAgent,
 ticketCategory?: string,
 ticketLanguage?: string
): number => {
 const agentDept = agent.department || 'general_support'
 const agentLangs: string[] = Array.isArray(agent.supported_languages) ? agent.supported_languages : ['en']
 const lang = ticketLanguage || 'en'
 const cat = ticketCategory || 'general_support'

 const deptMatch = agentDept === cat
 const langMatch = agentLangs.includes(lang)

 if (deptMatch && langMatch) return 3
 if (deptMatch) return 2
 if (agentDept === 'general_support') return 1
 return 0
}

export const assignAgentToTicket = async (ticketId: string, agentId?: string): Promise<boolean> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 if (!agentId) {
 // Auto-assign using weighted skill-based matching
 const ticketResult = await callSupportAPI('getTicketById', { ticketId })
 if (!ticketResult.success || !ticketResult.ticket) return false

 const ticket = ticketResult.ticket
 const availableAgents = await getAvailableAgents(ticket.user_type)
 if (availableAgents.length === 0) return false

 // Score each agent and sort by score desc, priority asc, load asc
 const scored = availableAgents
  .map(a => ({
   agent: a,
   score: scoreAgentForTicket(a, ticket.category, ticket.language),
   load: Array.isArray(a.availability)
    ? (a.availability[0]?.current_chat_count || 0)
    : ((a.availability as AgentAvailability)?.current_chat_count || 0)
  }))
  .filter(s => s.score > 0) // exclude agents with zero relevance
  .sort((a, b) => {
   if (b.score !== a.score) return b.score - a.score
   if (a.agent.priority_level !== b.agent.priority_level) return a.agent.priority_level - b.agent.priority_level
   return a.load - b.load
  })

 // Fall back to first available if no scored match
 agentId = scored.length > 0 ? String(scored[0].agent.id) : String(availableAgents[0].id)
 }

 // v2.9.56: Use dedicated server-side auto-assignment operation
 // This bypasses the user field whitelist securely while verifying ticket ownership
 const assignResult = await callSupportAPI('auto_assign_ticket', {
 ticketId,
 agentId
 })

 if (!assignResult.success) throw new Error(assignResult.error || 'Assignment failed')

 return assignResult.assigned !== false
 } catch (error) {
 console.error('Error assigning agent to ticket:', error)
 return false
 }
}

// Ticket Management
export const createSupportTicket = async (
 userId: number,
 title: string,
 description: string,
 category?: string,
 priority: 'low' | 'medium' | 'high' | 'urgent' = 'medium'
): Promise<SupportTicket | null> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const userType = await getUserType(userId)
 const ticketNumber = generateTicketNumber()

 const result = await callSupportAPI('createTicket', {
 ticketData: {
 ticket_number: ticketNumber,
 user_id: userId,
 user_type: userType,
 title,
 description,
 category,
 priority,
 status: 'open'
 }
 })

 if (!result.success) throw new Error(result.error)

 const data = result.ticket

 // Try to auto-assign an agent
 await assignAgentToTicket(data.id)

 // Send email notification to support team
 try {
 console.log('Sending new ticket notification email...')
 const notificationResult = await sendNewTicketNotification({
 id: data.id,
 ticket_number: ticketNumber,
 user_id: userId,
 title,
 description,
 priority,
 category: category || 'general',
 user_type: userType
 })

 if (notificationResult.success) {
 console.log('New ticket notification sent successfully')
 } else {
 console.warn('Failed to send new ticket notification:', notificationResult.error)
 }
 } catch (emailError) {
 console.error('Error sending new ticket notification:', emailError)
 // Don't fail ticket creation if email fails
 }

 return data
 } catch (error) {
 console.error('Error creating support ticket:', error)
 return null
 }
}

export const getUserTickets = async (userId: number): Promise<SupportTicket[]> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('getUserTickets', { userId })

 if (!result.success) throw new Error(result.error)

 // Note: Agent details would need additional API calls or be included in the API response
 // For now, returning tickets without agent details (can enhance API if needed)
 return result.tickets || []
 } catch (error) {
 console.error('Error getting user tickets:', error)
 return []
 }
}

export const getAgentTickets = async (agentId: string): Promise<SupportTicket[]> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('getAgentTickets', { agentId })

 if (!result.success) throw new Error(result.error)
 return result.tickets || []
 } catch (error) {
 console.error('Error getting agent tickets:', error)
 return []
 }
}

export const updateTicketStatus = async (
 ticketId: string,
 status: 'open' | 'in_progress' | 'waiting_user' | 'resolved' | 'closed',
 agentId?: string,
 responseMessage?: string
): Promise<boolean> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('updateTicket', {
 ticketId,
 updates: {
 status,
 updated_at: new Date().toISOString()
 }
 })

 if (!result.success) throw new Error(result.error)

 // Send email notification to user about status change
 try {
 console.log('Sending ticket status update notification...')
 const notificationResult = await sendTicketResponseNotification(ticketId, {
 statusUpdate: status,
 responseMessage,
 agentId
 })

 if (notificationResult.success) {
 console.log('Ticket status update notification sent successfully')
 } else {
 console.warn('Failed to send status update notification:', notificationResult.error)
 }
 } catch (emailError) {
 console.error('Error sending status update notification:', emailError)
 // Don't fail status update if email fails
 }

 return true
 } catch (error) {
 console.error('Error updating ticket status:', error)
 return false
 }
}

export const addTicketResponse = async (
 ticketId: string,
 responseMessage: string,
 agentId: string
): Promise<boolean> => {
 try {
 // Add response to ticket (you might want to create a ticket_responses table)
 // For now, we'll just send the notification

 // Send email notification to user about the response
 try {
 console.log('Sending ticket response notification...')
 const notificationResult = await sendTicketResponseNotification(ticketId, {
 responseMessage,
 agentId
 })

 if (notificationResult.success) {
 console.log('Ticket response notification sent successfully')
 } else {
 console.warn('Failed to send response notification:', notificationResult.error)
 }
 } catch (emailError) {
 console.error('Error sending response notification:', emailError)
 // Don't fail response addition if email fails
 }

 return true
 } catch (error) {
 console.error('Error adding ticket response:', error)
 return false
 }
}

// Chat Session Management
export const createChatSession = async (
 ticketId: string,
 userId: number,
 agentId?: string,
 sessionType: 'support' | 'consultation' | 'training' = 'support'
): Promise<ChatSession | null> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('createChatSession', {
 sessionData: {
 ticket_id: ticketId,
 user_id: userId,
 agent_id: agentId,
 session_type: sessionType,
 status: 'active'
 }
 })

 if (!result.success) throw new Error(result.error)
 return result.session
 } catch (error) {
 console.error('Error creating chat session:', error)
 return null
 }
}

export const sendChatMessage = async (
 sessionId: string,
 ticketId: string,
 senderType: 'user' | 'agent' | 'system',
 senderId: number,
 content: string,
 messageType: 'text' | 'file' | 'image' | 'system_notification' = 'text',
 fileUrl?: string,
 fileName?: string,
 fileSize?: number,
 isInternalNote: boolean = false
): Promise<ChatMessage | null> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('addChatMessage', {
 messageData: {
 session_id: sessionId,
 ticket_id: ticketId,
 sender_type: senderType,
 sender_id: senderId,
 message_type: messageType,
 content,
 file_url: fileUrl,
 file_name: fileName,
 file_size: fileSize,
 is_internal_note: isInternalNote
 }
 })

 if (!result.success) throw new Error(result.error)

 // Update ticket's first response time if this is the first agent response
 if (senderType === 'agent') {
 await callSupportAPI('updateTicket', {
 ticketId,
 updates: {
 first_response_at: new Date().toISOString(),
 updated_at: new Date().toISOString()
 }
 })
 }

 return result.message
 } catch (error) {
 console.error('Error sending chat message:', error)
 return null
 }
}

export const getChatMessages = async (sessionId: string): Promise<ChatMessage[]> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('getChatMessages', { sessionId })

 if (!result.success) throw new Error(result.error)
 return result.messages || []
 } catch (error) {
 console.error('Error getting chat messages:', error)
 return []
 }
}

// Training Content Management
export const getTrainingContent = async (
 _userType: 'shareholder' | 'affiliate',
 categoryId?: string
): Promise<TrainingContent[]> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('getTrainingContent', { categoryId })

 if (!result.success) throw new Error(result.error)
 return result.courses || []
 } catch (error) {
 console.error('Error getting training content:', error)
 return []
 }
}

export const getUserTrainingProgress = async (
 userId: number,
 courseId?: string
): Promise<UserTrainingProgress[]> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('getUserTrainingProgress', { userId, courseId })

 if (!result.success) throw new Error(result.error)
 return result.progress || []
 } catch (error) {
 console.error('Error getting user training progress:', error)
 return []
 }
}

export const updateTrainingProgress = async (
 userId: number,
 contentId: string,
 progressPercentage: number,
 timeSpentMinutes: number,
 status?: 'not_started' | 'in_progress' | 'completed' | 'failed'
): Promise<boolean> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const updateData: any = {
 progress_percentage: progressPercentage,
 time_spent_minutes: timeSpentMinutes,
 last_accessed_at: new Date().toISOString(),
 updated_at: new Date().toISOString()
 }

 if (status) {
 updateData.status = status
 if (status === 'in_progress' && progressPercentage === 0) {
 updateData.started_at = new Date().toISOString()
 }
 if (status === 'completed') {
 updateData.completed_at = new Date().toISOString()
 }
 }

 const result = await callSupportAPI('updateTrainingProgress', {
 userId,
 courseId: contentId,
 updates: updateData
 })

 if (!result.success) throw new Error(result.error)
 return true
 } catch (error) {
 console.error('Error updating training progress:', error)
 return false
 }
}

// Consultation Booking Management
export const createConsultationBooking = async (
 userId: number,
 consultationType: 'investment_guidance' | 'business_opportunity' | 'technical_support',
 title: string,
 description: string,
 scheduledAt: string,
 durationMinutes: number = 30,
 timezone: string = 'UTC'
): Promise<ConsultationBooking | null> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const userType = await getUserType(userId)

 // Find available agent for the consultation type
 const availableAgents = await getAvailableAgents(userType)
 const agentId = availableAgents.length > 0 ? availableAgents[0].id : null

 const result = await callSupportAPI('createConsultation', {
 consultationData: {
 user_id: userId,
 agent_id: agentId,
 consultation_type: consultationType,
 title,
 description,
 scheduled_at: scheduledAt,
 duration_minutes: durationMinutes,
 timezone,
 status: 'scheduled'
 }
 })

 if (!result.success) throw new Error(result.error)
 return result.booking
 } catch (error) {
 console.error('Error creating consultation booking:', error)
 return null
 }
}

export const getUserConsultations = async (userId: number): Promise<ConsultationBooking[]> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('getUserConsultations', { userId })

 if (!result.success) throw new Error(result.error)

 // Note: Agent details would need additional API calls or be included in the API response
 // For now, returning consultations without agent details (can enhance API if needed)
 return result.consultations || []
 } catch (error) {
 console.error('Error getting user consultations:', error)
 return []
 }
}

// Ticket Response Types
export interface TicketResponse {
 id: number
 ticket_id: number
 responder_type: 'agent' | 'user' | 'system'
 responder_id: number | null
 message: string
 is_internal_note: boolean
 attachments: TicketAttachment[]
 created_at: string
 responder?: {
 id: number
 username?: string
 agent_name?: string
 email: string
 }
}

export interface TicketAttachment {
 url: string
 signedUrl?: string
 publicUrl?: string
 bucket?: string
 path?: string
 name: string
 size: number
 type: string
}

// Get ticket responses for a specific ticket
export const getTicketResponses = async (ticketId: number | string): Promise<TicketResponse[]> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('getTicketResponses', { ticketId })

 if (!result.success) throw new Error(result.error)

 // Note: Responder enrichment is handled server-side in the API
 return result.responses || []
 } catch (error) {
 console.error('Error getting ticket responses:', error)
 return []
 }
}

// Create a user response to a ticket
export const createUserTicketResponse = async (
 ticketId: number | string,
 userId: number,
 message: string,
 attachments: TicketAttachment[] = []
): Promise<TicketResponse | null> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('addTicketResponse', {
 responseData: {
 ticket_id: ticketId,
 responder_type: 'user',
 responder_id: userId,
 message: message.trim(),
 is_internal_note: false,
 attachments: attachments
 }
 })

 if (!result.success) throw new Error(result.error)

 // Update ticket's updated_at timestamp and set status to 'open' if it was 'waiting_user'
 // This is handled in the API now, but we can also call updateTicket
 await callSupportAPI('updateTicket', {
 ticketId,
 updates: {
 updated_at: new Date().toISOString()
 }
 })

 return result.response
 } catch (error) {
 console.error('Error creating user ticket response:', error)
 return null
 }
}

// Count open tickets for a user (for enforcing 5 ticket limit)
export const countUserOpenTickets = async (userId: number): Promise<number> => {
 try {
 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callSupportAPI('countUserOpenTickets', { userId })

 if (!result.success) throw new Error(result.error)
 return result.count || 0
 } catch (error) {
 console.error('Error counting user open tickets:', error)
 return 0
 }
}

// Allowed MIME types and their corresponding safe extensions
const ALLOWED_FILE_TYPES: { [key: string]: string } = {
 'image/jpeg': 'jpg',
 'image/jpg': 'jpg',
 'image/png': 'png',
 'image/gif': 'gif',
 'image/webp': 'webp',
 'application/pdf': 'pdf'
}

// Magic bytes for file type validation
const FILE_SIGNATURES: { [key: string]: number[] } = {
 'image/jpeg': [0xFF, 0xD8, 0xFF],
 'image/png': [0x89, 0x50, 0x4E, 0x47],
 'image/gif': [0x47, 0x49, 0x46],
 'image/webp': [0x52, 0x49, 0x46, 0x46], // RIFF header (WebP starts with RIFF)
 'application/pdf': [0x25, 0x50, 0x44, 0x46] // %PDF
}

// Validate file by checking magic bytes
const validateFileMagicBytes = async (file: File): Promise<boolean> => {
 return new Promise((resolve) => {
 const reader = new FileReader()
 reader.onloadend = (e) => {
 if (!e.target?.result) {
 resolve(false)
 return
 }

 const arr = new Uint8Array(e.target.result as ArrayBuffer)
 const signature = FILE_SIGNATURES[file.type]

 if (!signature) {
 resolve(false)
 return
 }

 // Check if file starts with expected magic bytes
 const matches = signature.every((byte, index) => arr[index] === byte)
 resolve(matches)
 }
 reader.onerror = () => resolve(false)
 // Read first 8 bytes (enough for all our signatures)
 reader.readAsArrayBuffer(file.slice(0, 8))
 })
}

// Sanitize filename to prevent path traversal and injection attacks
const sanitizeFileName = (name: string): string => {
 // Remove path separators and null bytes
 return name
 .replace(/[\/\\]/g, '_')
 .replace(/\0/g, '')
 .replace(/\.\./g, '_')
 .substring(0, 100) // Limit filename length
}

// Upload ticket attachment to storage with security validations
export const uploadTicketAttachment = async (
 file: File,
 ticketId: number | string,
 userId: number
): Promise<TicketAttachment | null> => {
 try {
 // Use regular authenticated client - NOT service role (security risk in browser)
 // RLS policy "Allow authenticated users to upload ticket attachments" handles access control

 // SECURITY: Validate file type against whitelist
 if (!ALLOWED_FILE_TYPES[file.type]) {
 console.error('Security: Invalid file type rejected:', file.type)
 return null
 }

 // SECURITY: Validate file size (5MB max)
 const maxSize = 5 * 1024 * 1024
 if (file.size > maxSize) {
 console.error('Security: File too large:', file.size)
 return null
 }

 // SECURITY: Validate magic bytes to prevent MIME type spoofing
 const validMagicBytes = await validateFileMagicBytes(file)
 if (!validMagicBytes) {
 console.error('Security: File magic bytes do not match declared MIME type. Possible file type spoofing detected.')
 return null
 }

 // SECURITY: Use whitelisted extension instead of user-provided extension
 const safeExtension = ALLOWED_FILE_TYPES[file.type]

 // Generate unique filename with safe extension
 const timestamp = Date.now()
 const randomStr = Math.random().toString(36).substring(2, 8)
 const sanitizedName = sanitizeFileName(file.name)
 const fileName = `ticket-${ticketId}/user-${userId}/${timestamp}-${randomStr}.${safeExtension}`

 console.log('Uploading file:', {
 fileName,
 mimeType: file.type,
 size: file.size,
 dataType: 'File'
 })

 // Pass the File object directly - Supabase client handles File objects correctly
 const { data, error } = await supabase.storage
 .from('ticket-attachments')
 .upload(fileName, file, {
 cacheControl: '3600',
 upsert: false
 })

 if (error) throw error

 // Get public URL
 const { data: urlData } = supabase.storage
 .from('ticket-attachments')
 .getPublicUrl(data.path)

 console.log('Secure file upload successful:', sanitizedName)

 return {
 url: urlData.publicUrl,
 publicUrl: urlData.publicUrl,
 bucket: 'ticket-attachments',
 path: data.path,
 name: sanitizedName,
 size: file.size,
 type: file.type
 }
 } catch (error) {
 console.error('Error uploading ticket attachment:', error)
 return null
 }
}
